import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { auditTitleByEntity, EmbedLog, EmbedLogWithContent } from '@data/models/audit.model';
import { AuditFilterParams, AuditFilters, AuditPagination, AuditService } from '@data/services/audit.service';
import { Observable, of, Subscription, zip } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

@Component({

    selector: 'syn-audit-entity',
    templateUrl: './audit-entity.component.html',
    styleUrls: ['./audit-entity.component.scss']
})
export class AuditEntityComponent implements OnInit, OnChanges, OnDestroy {

    @Input() public resourceId: string;

    @Input() public entity: string;

    @Input() public filter: Record<string, string>;

    public loading = true;
    public loadingLog = false;

    public logs: EmbedLog<'$who'>[] = [];
    public log?: EmbedLogWithContent<'$who' | '$previousWho'>;
    public nextLog?: EmbedLog<'$who'>;
    public previousLog?: EmbedLog<'$who'>;

    public filters: AuditFilters = {};
    public pagination: AuditPagination = {
        page: 1,
        limit: 10
    };
    public title = '';

    private fetchLogsSubscription: Subscription;

    private fetchLogSubscription: Subscription;

    constructor(
        private auditService: AuditService,
        private router: Router,
        private route: ActivatedRoute,
    ) { }

    public ngOnInit(): void {
        this.fetchLogSubscription = this.route.queryParams.pipe(
            tap(() => this.loadingLog = true),
            switchMap(({ logId }) => {
                if (!logId) {
                    return of(undefined);
                }

                return this.fetchLog(logId);
            }),
            switchMap(log => {
                if (log !== undefined) {
                    return zip(of(log), this.getPreviousLog(log), this.getNextLog(log));
                } else {
                    return of([undefined, undefined, undefined]);
                }
            })
        ).subscribe(([log, previousLog, nextLog]) => {
            this.log = log;
            this.previousLog = previousLog;
            this.nextLog = nextLog;
            this.loadingLog = false;
        });
    }

    public async ngOnChanges(simpleChange: SimpleChanges): Promise<void> {
        this.fetchLogs(true);
        if (simpleChange.entity) {
            this.title = this.getTitleByEntity();
            await this.router.navigate([], {
                relativeTo: this.route,
                preserveFragment: true,
                queryParamsHandling: 'preserve',
            });
        }
    }

    public ngOnDestroy(): void {
        if (this.fetchLogSubscription) {
            this.fetchLogSubscription.unsubscribe();
        }

        if (this.fetchLogsSubscription) {
            this.fetchLogsSubscription.unsubscribe();
        }
    }

    public async selectLog(logId?: string): Promise<void> {
        await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { logId },
            preserveFragment: true,
        });
    }

    public setPagination(pagination: AuditPagination): void {
        this.pagination = pagination;
        this.fetchLogs();
    }

    public async setFilters(filters: AuditFilters): Promise<void> {
        if (JSON.stringify(this.filters) !== JSON.stringify(filters)) {
            this.filters = filters;
            this.pagination.page = 1;
            this.fetchLogs(true);
            return this.selectLog(undefined);
        }
    }

    public async loadPreviousLog(): Promise<void> {
        if (this.previousLog) {
            return this.selectLog(this.previousLog.id);
        }
    }

    public async loadNextLog(): Promise<void> {
        if (this.nextLog) {
            return this.selectLog(this.nextLog.id);
        }
    }

    private getTitleByEntity() {
        return auditTitleByEntity[this.entity];
    }

    private getPreviousLog(log: EmbedLog<'$who'>): Observable<EmbedLog<'$who'> | undefined> {
        return this.auditService.getPreviousLog(
            {
                entity: this.entity,
                resourceId: this.resourceId,
            },
            log.when,
            this.getFilterParams(),
            ['$who']
        );
    }

    private getNextLog(log: EmbedLog<'$who'>): Observable<EmbedLog<'$who'> | undefined> {
        return this.auditService.getNextLog(
            {
                entity: this.entity,
                resourceId: this.resourceId,
            },
            log.when,
            this.getFilterParams(),
            ['$who']
        );
    }

    private fetchLogs(clean: boolean = false): void {
        this.loading = true;

        if (clean) {
            this.logs = [];
        }

        if (this.fetchLogsSubscription) {
            this.fetchLogsSubscription.unsubscribe();
        }

        this.fetchLogsSubscription = this.auditService.getFilteredLogs(
            {
                entity: this.entity,
                resourceId: this.resourceId,
            },
            this.getFilterParams(),
            ['$who'],
        ).subscribe(logs => {
            this.logs = this.logs.concat(logs);
            this.loading = false;
        });
    }

    private fetchLog(logId: string): Observable<EmbedLogWithContent<'$who' | '$previousWho'>> {
        return this.auditService.getLog({
            logId,
            entity: this.entity,
            resourceId: this.resourceId,
        }, ['$who', '$previousWho']);
    }

    private getFilterParams(): AuditFilterParams {
        const params: AuditFilterParams = {
            sort: '-when',
            filter: this.filter,
            ...this.pagination,
        };

        if (this.filters.updatedFields) {
            params.updatedFields = this.filters.updatedFields.join(',');
        }

        if (this.filters.updatedBy) {
            params.who = this.filters.updatedBy;
        }

        if (this.filters.range?.from) {
            params.from = this.filters.range.from.toISOString();
        }

        if (this.filters.range?.to) {
            params.to = this.filters.range.to.toISOString();
        }

        return params;
    }
}
